import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createTeacher,
  deleteTeacher,
  getTeachersByClub,
} from "../../../services/teacherServices";
import { sendInvitation } from "../../../services/invitationServices";
import { getClubByUser, getClubById } from "../../../services/clubServices";
import { getDojosByClub } from "../../../services/dojoServices";
import { getGroupsByTeacher } from "../../../services/groupServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { isDemoUser } from "../../../utils/isDemoUser";
import useScrollToTop from "../../../hooks/useScollToTop";

import TeacherModal from "./TeacherModal";
import AssignTeacherModal from "./AssignTeacherModal";

import {
  FaArrowRight,
  FaChalkboardTeacher,
  FaEnvelope,
  FaFolderOpen,
  FaPlus,
  FaSearch,
  FaTrash,
  FaUserCircle,
  FaUsersCog,
  FaLayerGroup,
  FaMapMarkerAlt,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getInitials(teacher) {
  const nom = teacher?.nom?.[0] || "";
  const prenom = teacher?.prenom?.[0] || "";
  return `${prenom}${nom}`.toUpperCase() || "EN";
}

function getAssignedDojos(groups = []) {
  const dojoMap = new Map();

  groups.forEach((group) => {
    const dojoId = group?.dojo?.id ?? group?.dojo_id;
    const dojoName = group?.dojo?.nom_dojo || "Lieu inconnu";

    if (dojoId || dojoName) {
      dojoMap.set(String(dojoId ?? dojoName), {
        id: dojoId,
        nom_dojo: dojoName,
      });
    }
  });

  return [...dojoMap.values()];
}

const ManageTeachers = () => {
  useScrollToTop();

  const navigate = useNavigate();
  const { addNotification } = useNotification();

  let userInfo = null;
  try {
    userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
  } catch (error) {
    userInfo = null;
  }

  const userId = userInfo?.userId;
  const demoMode = isDemoUser(userInfo);

  const [club, setClub] = useState(null);
  const [clubId, setClubId] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [teacherGroups, setTeacherGroups] = useState({});
  const [dojos, setDojos] = useState([]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitingTeacherId, setInvitingTeacherId] = useState(null);
  const [deletingTeacherId, setDeletingTeacherId] = useState(null);

  const [teacherModalOpen, setTeacherModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDojoFilter, setSelectedDojoFilter] = useState("");

  useEffect(() => {
    if (userId) {
      fetchClub();
    } else {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (clubId) {
      fetchDojos(clubId);
      fetchTeachers(clubId);
    }
  }, [clubId]);

  const fetchClub = async () => {
    try {
      setLoading(true);

      const data = demoMode
        ? await getClubById(2)
        : await getClubByUser(userId);

      if (data?.id) {
        setClub(data);
        setClubId(data.id);
      } else {
        setClub(null);
        setClubId(null);
        setTeachers([]);
        setTeacherGroups({});
        setDojos([]);
      }
    } catch (error) {
      console.error("Erreur lors du chargement du club :", error);
      setClub(null);
      setClubId(null);
      setTeachers([]);
      setTeacherGroups({});
      setDojos([]);
      addNotification("Erreur lors du chargement du club.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchDojos = async (currentClubId) => {
    try {
      const data = await getDojosByClub(currentClubId);
      setDojos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des lieux :", error);
      setDojos([]);
      addNotification("Erreur lors du chargement des lieux.", "error");
    }
  };

  const fetchTeachers = async (currentClubId = clubId) => {
    if (!currentClubId) return;

    try {
      setLoading(true);

      const teachersData = await getTeachersByClub(currentClubId);
      const safeTeachers = Array.isArray(teachersData) ? teachersData : [];
      setTeachers(safeTeachers);

      const groupEntries = await Promise.all(
        safeTeachers.map(async (teacher) => {
          try {
            const groups = await getGroupsByTeacher(teacher.id);
            return [teacher.id, Array.isArray(groups) ? groups : []];
          } catch (error) {
            console.error(
              `Erreur lors du chargement des groupes de l'enseignant ${teacher.id} :`,
              error
            );
            return [teacher.id, []];
          }
        })
      );

      setTeacherGroups(Object.fromEntries(groupEntries));
    } catch (error) {
      console.error("Erreur lors du chargement des enseignants :", error);
      setTeachers([]);
      setTeacherGroups({});
      addNotification("Erreur lors du chargement des enseignants.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeacher = async (teacherFormData) => {
    if (demoMode) {
      addNotification("Mode Démo : ajout désactivé.", "warning");
      return false;
    }

    if (!clubId) {
      addNotification("Impossible de trouver le club.", "error");
      return false;
    }

    try {
      setSubmitting(true);

      await createTeacher({
        ...teacherFormData,
        club_id: clubId,
        role_id: 6,
      });

      addNotification("Enseignant ajouté avec succès.", "success");
      await fetchTeachers(clubId);

      return true;
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'enseignant :", error);
      addNotification("Erreur lors de l'ajout de l'enseignant.", "error");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendTeacherInvitation = async (teacher) => {
    if (demoMode) {
      addNotification("Mode Démo : invitation désactivée.", "warning");
      return;
    }

    if (!teacher?.email) {
      addNotification("Aucun email renseigné pour cet enseignant.", "error");
      return;
    }

    if (
      !window.confirm(
        `Envoyer une invitation à ${teacher.prenom || ""} ${teacher.nom || ""} ?`
      )
    ) {
      return;
    }

    try {
      setInvitingTeacherId(teacher.id);

      await sendInvitation({
        email: teacher.email,
        nom: teacher.nom,
        prenom: teacher.prenom,
        club_id: clubId,
      });

      addNotification(
        `Invitation envoyée à ${teacher.prenom || ""} ${teacher.nom || ""}.`,
        "success"
      );
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'invitation :", error);
      addNotification("Erreur lors de l'envoi de l'invitation.", "error");
    } finally {
      setInvitingTeacherId(null);
    }
  };

  const handleDeleteTeacher = async (teacherId) => {
    if (demoMode) {
      addNotification("Mode Démo : suppression désactivée.", "warning");
      return;
    }

    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet enseignant ?")) {
      return;
    }

    try {
      setDeletingTeacherId(teacherId);
      await deleteTeacher(teacherId);

      addNotification("Enseignant supprimé avec succès.", "success");
      await fetchTeachers(clubId);
    } catch (error) {
      console.error("Erreur lors de la suppression de l'enseignant :", error);
      addNotification(
        "Erreur lors de la suppression de l'enseignant.",
        "error"
      );
    } finally {
      setDeletingTeacherId(null);
    }
  };

  const openAssignModal = (teacher) => {
    if (demoMode) {
      addNotification("Mode Démo : attribution désactivée.", "warning");
      return;
    }

    setSelectedTeacher(teacher);
    setAssignModalOpen(true);
  };

  const filteredTeachers = useMemo(() => {
    return teachers.filter((teacher) => {
      const assignedGroups = teacherGroups[teacher.id] || [];
      const assignedDojos = getAssignedDojos(assignedGroups);

      const search = searchTerm.trim().toLowerCase();

      const teacherBlob = [
        teacher?.nom || "",
        teacher?.prenom || "",
        teacher?.email || "",
        ...assignedGroups.map((group) => group?.nom_groupe || ""),
        ...assignedDojos.map((dojo) => dojo?.nom_dojo || ""),
      ]
        .join(" ")
        .toLowerCase();

      const matchSearch = !search || teacherBlob.includes(search);

      const matchDojo =
        !selectedDojoFilter ||
        assignedGroups.some((group) => {
          const dojoId = group?.dojo?.id ?? group?.dojo_id;
          return String(dojoId) === String(selectedDojoFilter);
        });

      return matchSearch && matchDojo;
    });
  }, [teachers, teacherGroups, searchTerm, selectedDojoFilter]);

  const totalTeachers = teachers.length;
  const filteredCount = filteredTeachers.length;
  const totalAssignedGroups = Object.values(teacherGroups).reduce(
    (acc, groups) => acc + (Array.isArray(groups) ? groups.length : 0),
    0
  );

  const coveredDojosCount = useMemo(() => {
    const ids = new Set();

    Object.values(teacherGroups).forEach((groups) => {
      (groups || []).forEach((group) => {
        const dojoId = group?.dojo?.id ?? group?.dojo_id;
        if (dojoId) ids.add(String(dojoId));
      });
    });

    return ids.size;
  }, [teacherGroups]);

  return (
    <div className="w-full bg-slate-50 pb-12 pt-[100px] dark:bg-slate-950">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6">
        {/* Header */}
        <section className="mt-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Votre compte
              </p>
              <h1 className="mt-1 text-3xl font-black text-slate-900 dark:text-slate-100">
                Enseignants
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-400">
                Gérez vos enseignants, affectez-les aux lieux et groupes, puis
                envoyez rapidement leurs invitations.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {demoMode && (
                <span className="inline-flex rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                  Mode Démo
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Club + KPI */}
        <section className="mt-5">
          {loading ? (
            <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              <div className="animate-pulse rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mx-auto h-24 w-24 rounded-full bg-slate-200 dark:bg-slate-800" />
                <div className="mx-auto mt-4 h-5 w-40 rounded bg-slate-200 dark:bg-slate-800" />
              </div>

              <div className="animate-pulse rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                </div>
              </div>
            </div>
          ) : !club ? (
            <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                <FaChalkboardTeacher className="text-4xl" />
              </div>

              <h2 className="mt-5 text-2xl font-black text-slate-900 dark:text-slate-100">
                Aucun club trouvé
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                Créez d’abord votre club avant de gérer vos enseignants.
              </p>

              <button
                onClick={() => navigate("/votre-club")}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Aller à votre club
                <FaArrowRight />
              </button>
            </div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                    Club
                  </p>
                  <h2 className="mt-2 text-2xl font-black">{club.nom_club}</h2>
                  <p className="mt-2 text-sm text-white/80">
                    {club.departement_club || "Département non renseigné"}
                  </p>
                </div>

                <div className="p-6 text-center">
                  {club.logo_url ? (
                    <img
                      src={club.logo_url}
                      alt={club.nom_club}
                      className="mx-auto h-24 w-24 rounded-full border-4 border-white object-cover shadow-xl ring-1 ring-slate-200 dark:border-slate-900 dark:ring-slate-700"
                    />
                  ) : (
                    <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-slate-100 text-slate-500 shadow-xl dark:bg-slate-800 dark:text-slate-300">
                      <FaChalkboardTeacher className="text-4xl" />
                    </div>
                  )}

                  <p className="mt-5 text-sm text-slate-600 dark:text-slate-400">
                    {club.adresse_club || "Adresse non renseignée"}
                  </p>
                </div>
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaChalkboardTeacher />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Enseignants
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100">
                      {totalTeachers}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                      <FaSearch />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Résultats filtrés
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100">
                      {filteredCount}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                      <FaLayerGroup />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Affectations groupes
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100">
                      {totalAssignedGroups}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                      <FaMapMarkerAlt />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Lieux couverts
                    </p>
                    <p className="mt-2 text-3xl font-black text-slate-900 dark:text-slate-100">
                      {coveredDojosCount}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Toolbar */}
        {!loading && club && (
          <section className="mt-6 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex w-full flex-col gap-4 lg:flex-row">
                <div className="relative w-full lg:max-w-md">
                  <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  <input
                    type="text"
                    placeholder="Rechercher un enseignant, un email, un groupe..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                  />
                </div>

                <select
                  value={selectedDojoFilter}
                  onChange={(e) => setSelectedDojoFilter(e.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                >
                  <option value="">Tous les lieux</option>
                  {dojos.map((dojo) => (
                    <option key={dojo.id} value={dojo.id}>
                      {dojo.nom_dojo}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    if (demoMode) {
                      addNotification("Mode Démo : ajout désactivé.", "warning");
                    } else {
                      setTeacherModalOpen(true);
                    }
                  }}
                  disabled={demoMode || submitting}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 font-semibold text-white transition",
                    demoMode || submitting
                      ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                      : "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
                  )}
                >
                  <FaPlus />
                  Ajouter
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Desktop table */}
        {!loading && club && (
          <section className="mt-6 hidden rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:block">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
                Liste des enseignants
              </h2>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {filteredTeachers.length} résultat(s)
              </span>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-left text-sm dark:bg-slate-800">
                    <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                      Enseignant
                    </th>
                    <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                      Contact
                    </th>
                    <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                      Affectations
                    </th>
                    <th className="px-4 py-3 text-center font-bold text-slate-700 dark:text-slate-200">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredTeachers.length > 0 ? (
                    filteredTeachers.map((teacher) => {
                      const assignedGroups = teacherGroups[teacher.id] || [];
                      const assignedDojos = getAssignedDojos(assignedGroups);

                      return (
                        <tr
                          key={teacher.id}
                          className="border-t border-slate-200 align-top dark:border-slate-800"
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-sm font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-100">
                                {getInitials(teacher)}
                              </div>

                              <div>
                                <p className="font-bold text-slate-900 dark:text-slate-100">
                                  {teacher.prenom} {teacher.nom}
                                </p>
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                  Enseignant
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                            <div>{teacher.email || "Email non renseigné"}</div>
                          </td>

                          <td className="px-4 py-4">
                            <div className="space-y-3">
                              <div>
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                  Lieux
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {assignedDojos.length ? (
                                    assignedDojos.map((dojo) => (
                                      <span
                                        key={dojo.id || dojo.nom_dojo}
                                        className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                                      >
                                        {dojo.nom_dojo}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-sm text-slate-400">
                                      Aucun
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div>
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                  Groupes
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {assignedGroups.length ? (
                                    assignedGroups.map((group) => (
                                      <span
                                        key={group.id}
                                        className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                      >
                                        {group.nom_groupe}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-sm text-slate-400">
                                      Aucun
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-4">
                            <div className="flex flex-wrap justify-center gap-2">
                              <button
                                onClick={() => handleSendTeacherInvitation(teacher)}
                                disabled={
                                  demoMode ||
                                  invitingTeacherId === teacher.id ||
                                  !teacher.email
                                }
                                className={cn(
                                  "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition",
                                  demoMode ||
                                    invitingTeacherId === teacher.id ||
                                    !teacher.email
                                    ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                                    : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                                )}
                              >
                                <FaEnvelope />
                                {invitingTeacherId === teacher.id
                                  ? "Envoi..."
                                  : "Inviter"}
                              </button>

                              <button
                                onClick={() => openAssignModal(teacher)}
                                className={cn(
                                  "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition",
                                  demoMode
                                    ? "bg-slate-400 dark:bg-slate-700"
                                    : "bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600"
                                )}
                              >
                                <FaUsersCog />
                                Groupes
                              </button>

                              <button
                                onClick={() => handleDeleteTeacher(teacher.id)}
                                disabled={demoMode || deletingTeacherId === teacher.id}
                                className={cn(
                                  "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition",
                                  demoMode || deletingTeacherId === teacher.id
                                    ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                                    : "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                                )}
                              >
                                <FaTrash />
                                {deletingTeacherId === teacher.id
                                  ? "Suppression..."
                                  : "Supprimer"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-4 py-8 text-center text-slate-500 dark:text-slate-400"
                      >
                        Aucun enseignant trouvé.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Mobile cards */}
        {!loading && club && (
          <section className="mt-6 space-y-4 lg:hidden">
            {filteredTeachers.length > 0 ? (
              filteredTeachers.map((teacher) => {
                const assignedGroups = teacherGroups[teacher.id] || [];
                const assignedDojos = getAssignedDojos(assignedGroups);

                return (
                  <article
                    key={teacher.id}
                    className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-slate-100 text-lg font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-100">
                        {getInitials(teacher)}
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="text-xl font-black text-slate-900 dark:text-slate-100">
                          {teacher.prenom} {teacher.nom}
                        </h3>
                        <p className="mt-1 break-all text-sm text-slate-600 dark:text-slate-400">
                          {teacher.email || "Email non renseigné"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Lieux
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {assignedDojos.length ? (
                          assignedDojos.map((dojo) => (
                            <span
                              key={dojo.id || dojo.nom_dojo}
                              className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                            >
                              {dojo.nom_dojo}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-slate-400">Aucun</span>
                        )}
                      </div>
                    </div>

                    <div className="mt-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Groupes
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {assignedGroups.length ? (
                          assignedGroups.map((group) => (
                            <span
                              key={group.id}
                              className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              {group.nom_groupe}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-slate-400">Aucun</span>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => handleSendTeacherInvitation(teacher)}
                        disabled={
                          demoMode || invitingTeacherId === teacher.id || !teacher.email
                        }
                        className={cn(
                          "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition",
                          demoMode || invitingTeacherId === teacher.id || !teacher.email
                            ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                            : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                        )}
                      >
                        <FaEnvelope />
                        {invitingTeacherId === teacher.id ? "Envoi..." : "Inviter"}
                      </button>

                      <button
                        onClick={() => openAssignModal(teacher)}
                        className={cn(
                          "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition",
                          demoMode
                            ? "bg-slate-400 dark:bg-slate-700"
                            : "bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600"
                        )}
                      >
                        <FaUsersCog />
                        Groupes
                      </button>

                      <button
                        onClick={() => handleDeleteTeacher(teacher.id)}
                        disabled={demoMode || deletingTeacherId === teacher.id}
                        className={cn(
                          "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition",
                          demoMode || deletingTeacherId === teacher.id
                            ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                            : "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                        )}
                      >
                        <FaTrash />
                        {deletingTeacherId === teacher.id
                          ? "Suppression..."
                          : "Supprimer"}
                      </button>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="rounded-[24px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                  <FaFolderOpen className="text-2xl" />
                </div>
                <h3 className="mt-4 text-xl font-black text-slate-900 dark:text-slate-100">
                  Aucun enseignant trouvé
                </h3>
              </div>
            )}
          </section>
        )}
      </div>

      <TeacherModal
        isOpen={teacherModalOpen}
        onClose={() => setTeacherModalOpen(false)}
        onSave={handleAddTeacher}
        demoMode={demoMode}
      />

      <AssignTeacherModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        teacher={selectedTeacher}
        dojos={dojos}
        initialGroups={selectedTeacher ? teacherGroups[selectedTeacher.id] || [] : []}
        onSaved={(resolvedGroups) => {
          if (!selectedTeacher) return;
          setTeacherGroups((prev) => ({
            ...prev,
            [selectedTeacher.id]: resolvedGroups,
          }));
        }}
        refreshTeachers={() => fetchTeachers(clubId)}
        demoMode={demoMode}
      />
    </div>
  );
};

export default ManageTeachers;